import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, CheckCircle, AlertCircle, RefreshCw, Clock, 
  Building2, CreditCard, Calendar, User, Receipt, Shield,
  ArrowRight, XCircle, Loader2
} from 'lucide-react';

const API = `${window.location.origin}/api`;

const GuestInvoicePayment = () => {
  const { paymentToken } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [balanceDue, setBalanceDue] = useState(0);
  
  // Check if returning from payment
  const status = searchParams.get('status');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (status === 'success' && sessionId) {
      // Check payment status
      checkPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      setPaymentStatus('cancelled');
      fetchInvoice();
    } else {
      fetchInvoice();
    }
  }, [paymentToken, status, sessionId]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/einvoice/guest/invoice/${paymentToken}`);
      setInvoice(res.data.invoice);
      setBalanceDue(res.data.balance_due);
      
      if (res.data.already_paid) {
        setPaymentStatus('paid');
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      if (err.response?.status === 404) {
        setError('not_found');
      } else if (err.response?.status === 410) {
        setError('expired');
      } else {
        setError('unknown');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sid) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/einvoice/guest/payment-status/${sid}`);
      
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        toast.success('Payment successful! Thank you.');
      } else {
        setPaymentStatus('pending');
      }
      
      // Fetch invoice details
      await fetchInvoice();
    } catch (err) {
      console.error('Error checking payment status:', err);
      await fetchInvoice();
    }
  };

  const handlePayNow = async () => {
    try {
      setProcessing(true);
      
      const res = await axios.post(`${API}/einvoice/guest/create-payment`, {
        payment_token: paymentToken,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (err) {
      console.error('Error creating payment:', err);
      toast.error(err.response?.data?.detail || 'Failed to process payment');
      setProcessing(false);
    }
  };

  const formatCurrency = (amount, currency = 'MYR') => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Payment Link Not Found</h2>
            <p className="text-slate-600">
              This payment link is invalid or has been removed. 
              Please contact the hotel for a new payment link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Payment Link Expired</h2>
            <p className="text-slate-600">
              This payment link has expired for security reasons. 
              Please contact the hotel for a new payment link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus === 'paid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for your payment. Your invoice has been marked as paid.
            </p>
            {invoice && (
              <div className="bg-slate-50 rounded-xl p-4 text-left">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-500 text-sm">Invoice Number</span>
                  <span className="font-mono font-bold text-blue-600">{invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 text-sm">Amount Paid</span>
                  <span className="font-bold text-green-600">{formatCurrency(invoice.total, invoice.currency)}</span>
                </div>
              </div>
            )}
            <p className="text-sm text-slate-500 mt-6">
              A receipt will be sent to your email address.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus === 'cancelled') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4" data-testid="guest-payment-page">
        <div className="max-w-lg mx-auto">
          {/* Cancelled Banner */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-yellow-800 text-sm">
              Payment was cancelled. You can try again when you're ready.
            </p>
          </div>
          
          {/* Continue with invoice display */}
          {renderInvoiceCard()}
        </div>
      </div>
    );
  }

  const renderInvoiceCard = () => (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3">
          <Receipt className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Pay Your Invoice</h1>
        <p className="text-slate-600 text-sm mt-1">Secure payment powered by Stripe</p>
      </div>

      {/* Invoice Card */}
      <Card className="shadow-xl border-0 overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <CardDescription className="text-blue-100">Invoice</CardDescription>
              <CardTitle className="text-white font-mono text-lg">
                {invoice?.invoice_number || 'Loading...'}
              </CardTitle>
            </div>
            <Badge className="bg-white/20 text-white hover:bg-white/30">
              {invoice?.payment_status === 'paid' ? 'Paid' : 'Due'}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Property & Guest Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Property</p>
                <p className="font-medium text-slate-900">{invoice?.property_name || '-'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Guest</p>
                <p className="font-medium text-slate-900">{invoice?.guest_name || '-'}</p>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Stay Period</span>
            </div>
            <div className="flex justify-between text-sm">
              <div>
                <p className="text-slate-500">Check-in</p>
                <p className="font-medium">{formatDate(invoice?.check_in_date)}</p>
              </div>
              <div className="text-right">
                <p className="text-slate-500">Check-out</p>
                <p className="font-medium">{formatDate(invoice?.check_out_date)}</p>
              </div>
            </div>
          </div>

          {/* Line Items (if available) */}
          {invoice?.line_items && invoice.line_items.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Charges</h4>
              <div className="space-y-2">
                {invoice.line_items.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-slate-600">{item.description || item.name}</span>
                    <span className="font-medium">{formatCurrency(item.amount || item.total, invoice.currency)}</span>
                  </div>
                ))}
                {invoice.line_items.length > 5 && (
                  <p className="text-xs text-slate-500 text-center">
                    + {invoice.line_items.length - 5} more items
                  </p>
                )}
              </div>
            </div>
          )}

          <Separator className="my-4" />

          {/* Total */}
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-slate-900">Total Due</span>
            <span className="text-2xl font-bold text-blue-600">
              {formatCurrency(balanceDue, invoice?.currency)}
            </span>
          </div>
        </CardContent>

        <CardFooter className="bg-slate-50 p-6 flex flex-col gap-4">
          <Button 
            size="lg" 
            className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
            onClick={handlePayNow}
            disabled={processing}
            data-testid="pay-now-btn"
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Pay {formatCurrency(balanceDue, invoice?.currency)}
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
          
          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
            <Shield className="w-4 h-4" />
            <span>Secured by Stripe. Your payment info is encrypted.</span>
          </div>
        </CardFooter>
      </Card>

      {/* Footer */}
      <div className="text-center mt-6 text-sm text-slate-500">
        <p>Powered by StayMii e-Invoice Platform</p>
        <p className="text-xs mt-1">If you have questions, please contact the hotel directly.</p>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4" data-testid="guest-payment-page">
      <div className="max-w-lg mx-auto">
        {renderInvoiceCard()}
      </div>
    </div>
  );
};

export default GuestInvoicePayment;
